import { useCallback, useEffect, useMemo } from 'react';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { type MenuInfo } from 'rc-menu/lib/interface';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useUsersListSlice } from '@restore/usersList/usersList.slice';
import { useTeamFiltersSlice } from '@restore/teamFilters/teamFilters.slice';
import { PossibleFilters } from '@restore/teamFilters/teamFilters.types';
import { StateFilterItem } from '@components/NestedFilter/types';

export const useClinicalTrialTeamFilters = ({ id }: { id: string }) => {
  const [
    state,
    toggleActiveFilter,
    allFilters,
    toggleSelectedFilter,
    selectedFilters,
    fetchInstitutions,
    setSites,
    reset,
  ] = useTeamFiltersSlice((state, actions) => [
    state.state,
    actions.toggleActiveFilters,
    state.allFilters,
    actions.toggleSelectedFilters,
    state.selectedFilters,
    actions.fetchInstitutions,
    actions.setSites,
    actions.reset,
  ]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const clinicalTrialSites = useClinicalTrialSlice(
    state => state.data?.sites ?? []
  );

  const subFilters = useMemo(() => {
    return Object.entries(state) as [PossibleFilters, StateFilterItem][];
  }, [state]);

  const filters = useMemo(() => {
    return [
      {
        key: '2',
        type: 'group' as const,
        label: (
          <div className='text-1216 text-spanishGray'>Set up your Filters</div>
        ),
        children: Object.entries(state).map(([key, filter]) => ({
          key,
          label: (
            <div className='flex justify-between'>
              <CustomTooltip label={filter.label} MAX_LABEL_LENGTH={18} />
              <Checkbox checked={state?.[key as PossibleFilters]?.active} />
            </div>
          ),
        })),
      },
    ];
  }, [state]);

  const filterOnClick = useCallback(
    (item: MenuInfo) => {
      toggleActiveFilter(item?.key as PossibleFilters);
    },
    [toggleActiveFilter]
  );

  const args = useMemo(
    () => ({
      institution: selectedFilters?.institution?.[0],
      role: selectedFilters?.role?.[0],
      siteCode: selectedFilters?.site?.[0],
    }),
    [selectedFilters]
  );

  const institutions = useUsersListSlice(state => state.institutions);

  useEffect(() => {
    if (institutions?.length) {
      fetchInstitutions?.(id);
    }
  }, [id, institutions?.length]);

  useEffect(() => {
    if (clinicalTrialSites.length && setSites) {
      setSites(clinicalTrialSites);
    }
  }, [clinicalTrialSites]);

  return {
    subFilters,
    filters,
    allFilters,
    toggleActiveFilter,
    toggleSelectedFilter,
    selectedFilters,
    filterOnClick,
    args,
    fetchInstitutions,
  };
};
